import clsx from "clsx";
import { getExperienceMin } from "common";
import { isEqual, sortBy } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { CatalogItem, Experience, Guest, LineItemTemplate, LineItemType, OPEN_UNLIMITED, UnitType } from "types";
import { useDebouncedCallback } from "use-debounce";
import shallow from "zustand/shallow";
import { useResource } from "../../hooks/useResource";
import { getGuestsWithSameUnit } from "../../models/Guest";
import { isGift } from "../../models/Purchase";
import { DemographicLineItem, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { NoTranslate } from "../Common/NoTranslate";
import { QuantitySpinner } from "../Common/QuantitySpinner/QuantitySpinner";
import { getAnsweredFieldsCountForGuest, RemoveGuestModal } from "./RemoveGuestModal";
import { useSeller } from "@/hooks/useAccount";
import { getCatalogItemsByUnitType } from "@/models/Catalog";

export const CreatePurchaseDemographicPicker = React.memo(() => {
    const timeSlotOpenCount = useCreatePurchaseStore((state) => state.getActiveItem().timeslot?.capacity.open);

    const totalQuantity = useCreatePurchaseStore((state) => state.getActiveItem().quantity);

    const isActiveWaitlistWorkflow = useCreatePurchaseStore((state) => state.isWaitListMode());
    const experience = useCreatePurchaseStore((state) => state.getActiveProduct<Experience>());
    const demographicCatalogItems = getCatalogItemsByUnitType(experience.catalog.items, UnitType.DEMOGRAPHIC);
    const sortedDemographicCatalogItems = useMemo(
        () => sortBy(demographicCatalogItems, "sequence"),
        [demographicCatalogItems],
    );
    let { orderMax = OPEN_UNLIMITED, outingMax = OPEN_UNLIMITED } = experience.group ?? {};
    if (orderMax > outingMax) {
        orderMax = outingMax;
    }

    const timeslotMaxOpenCapacity = timeSlotOpenCount ?? Math.min(orderMax, outingMax);
    const availabilityMax = isActiveWaitlistWorkflow
        ? Math.max(orderMax, outingMax, timeslotMaxOpenCapacity)
        : timeslotMaxOpenCapacity;

    const listingMax = isActiveWaitlistWorkflow ? outingMax : availabilityMax;
    const max = listingMax;

    const error = totalQuantity > max ? `Quantity cannot exceed ${max}` : undefined;

    return (
        <div className="space-y-2">
            <div className={clsx("text-sm font-bold", { "text-danger": error })}>{error ?? "Quantity"}</div>
            <div className="flex flex-col space-y-2">
                {sortedDemographicCatalogItems.map((demographicCatalogItem) => {
                    return (
                        <DemographicItem
                            key={`${experience.id}${demographicCatalogItem.id}`}
                            demographicCatalogItem={demographicCatalogItem}
                            max={max}
                            experience={experience}
                            error={error}
                        />
                    );
                })}
            </div>
        </div>
    );
});

interface DemographicItemProps {
    demographicCatalogItem: CatalogItem;
    max: number;
    experience: Experience;
    min?: number | null;
    error?: string;
}

export const DemographicItem = ({ demographicCatalogItem, max, experience, min, error }: DemographicItemProps) => {
    const demographic = useCreatePurchaseStore((state) => {
        const match = state
            .getDemographicLineItems()
            .find((lineItem) => lineItem.template?.id === demographicCatalogItem.template?.id);

        return { templateId: match?.template?.id ?? "", quantity: match?.quantity ?? 0 };
    }, shallow);

    const seller = useSeller();
    const quantity = demographic.quantity ?? 0;
    const [showRemoveGuestModal, setShowRemoveGuestModal] = useState(false);
    const isWaitListMode = useCreatePurchaseStore((state) => state.isWaitListMode());
    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
    const preventQuantitySync = useCreatePurchaseStore((state) => state.preventQuantitySync);
    const isPartnerExperience = experience.seller.id !== seller.id;
    const [quantityForWaitList, setQuantity] = useState(quantity);
    const [isGiftItem, isMist, totalDemographics] = useCreatePurchaseStore((state) => {
        const activeItem = state.getActiveItem();
        return [isGift(activeItem), state.isMist(), activeItem.quantity];
    }, shallow);

    useEffect(() => {
        // If the quantity changes in the waitlist modal, update it in this create purchase component too
        if (isWaitListMode && quantityForWaitList !== quantity) {
            setQuantity(quantity);
        }
    }, [isWaitListMode, quantityForWaitList, quantity]);

    const { data: demographicTemplate } = useResource<LineItemTemplate>(
        ["lineItemTemplates", demographic.templateId],
        null,
        { enabled: !isPartnerExperience },
    );

    const name = demographicCatalogItem?.name ?? "";

    // Calculate the minimum possible value for this demographic
    const minExperienceQuantity = min ?? (isGiftItem ? 1 : getExperienceMin(experience));
    const spare = totalDemographics - minExperienceQuantity;
    const minForDemographic = Math.max(quantity - spare, 0);

    const isCustom = !demographicCatalogItem?.template?.code;

    const changeQuantity = useDebouncedCallback(
        (newQuantity: number) => {
            if (!demographic.templateId) {
                return;
            }

            useCreatePurchaseStore
                .getState()
                .actions.updateQuantity(activeItemIndex, demographic.templateId, newQuantity, demographicTemplate);
        },
        100,
        { leading: true, trailing: true },
    );

    const handleDemographicChange = (quantity: number) => {
        if (useCreatePurchaseStore.getState().getActiveItem().parentProduct) {
            changeQuantity(quantity);
        } else if (shouldShowReduceQuantityPrompt(quantity)) {
            setShowRemoveGuestModal(true);
        } else {
            changeQuantity(quantity);
        }
    };

    /**
     * Gets the guest name with least form response answers.
     * Used to show the confirmation modal to remove the guest.
     * Returns `null` if there's no need to show the modal.
     */
    const shouldShowReduceQuantityPrompt = (nextQuantity: number) => {
        const item = useCreatePurchaseStore.getState().getActiveItem();
        const guestsWithSameUnit = getGuestsWithSameUnit(item, demographicTemplate);
        if (!guestsWithSameUnit || guestsWithSameUnit.length === 0) {
            return;
        }

        const [guestWithLeastAnswers] = sortBy(guestsWithSameUnit, (guest) => getAnsweredFieldsCountForGuest(guest));

        if (nextQuantity < quantity && quantity > 1 && getAnsweredFieldsCountForGuest(guestWithLeastAnswers) > 0) {
            return true;
        }

        return null;
    };

    const handleQuantityUpdate = (guestToRemove: Guest) => {
        const item = useCreatePurchaseStore.getState().getActiveItem();
        const guestsWithSameUnit = getGuestsWithSameUnit(item, demographicTemplate);
        const guests = item.guests?.filter((guest) => guest.id !== guestToRemove.id);
        const guestCountWithSameUnitLength = guestsWithSameUnit?.filter(
            (guest) => guest.id !== guestToRemove.id,
        ).length;
        changeQuantity(guestCountWithSameUnitLength ?? 0);
        useCreatePurchaseStore.getState().actions.updateGuests(activeItemIndex, guests ?? []);
        setShowRemoveGuestModal(false);
    };

    const handleRemoveGuestModalClose = () => {
        const previousPurchase = useCreatePurchaseStore.getState().purchase;
        if (previousPurchase) {
            let isUpdated = false;
            useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                const clonedItems = draft.items.map((item, index) => {
                    const previousDemographicLineItems = previousPurchase.items[index].demographicLineItems.map(
                        (item) => ({
                            template: { id: item.template.id },
                            quantity: item.quantity,
                            type: LineItemType.DEMOGRAPHIC,
                        }),
                    ) as DemographicLineItem[];

                    if (isEqual(previousDemographicLineItems, item.demographicLineItems)) {
                        return item;
                    }

                    isUpdated = true;
                    item.demographicLineItems = previousDemographicLineItems;

                    item.quantity = previousPurchase.items[index].quantity;
                    return { ...item };
                });
                if (isUpdated) {
                    draft.items = clonedItems;
                }
            });
            useCreatePurchaseStore.setState({
                preventQuantitySync: true,
            });
            setShowRemoveGuestModal(false);
        }
    };

    const description = demographicCatalogItem?.caption ?? "";
    const popoverParentId = `experience-${experience.id}-demographic-${demographic.templateId}`;

    return (
        <div
            id={popoverParentId}
            className="flex items-center justify-start"
            data-testid="create-purchase-demographic-picker-item"
        >
            <QuantitySpinner
                value={quantity}
                total={totalDemographics}
                min={minForDemographic}
                max={max}
                size="large"
                popoverParentId={popoverParentId}
                classNames={{ fieldset: "mr-3", label: "font-bold" }}
                lockAtMax={false}
                shouldSyncValue={!isWaitListMode && !isMist && !preventQuantitySync}
                onChange={handleDemographicChange}
            />

            <div className="min-w-0 flex-1">
                <NoTranslate if={isCustom} as="div" className={clsx("break-all font-bold", { "text-danger": error })}>
                    {name}
                </NoTranslate>
                {description.length > 0 && <div className="text-gray-darker line-clamp-2">{description}</div>}
            </div>

            {showRemoveGuestModal && (
                <RemoveGuestModal
                    demographic={demographic}
                    isOpen={showRemoveGuestModal}
                    experience={experience}
                    onRemove={handleQuantityUpdate}
                    onClose={handleRemoveGuestModalClose}
                />
            )}
        </div>
    );
};
